import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import Pagination from 'components/Pagination';
import { useDragAndReorder } from 'hooks/useDragAndReorder';
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { AiOutlineClear } from 'react-icons/ai';
import { FaLongArrowAltDown, FaLongArrowAltUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  loadObjectFromLocalStorage,
  saveObjectToLocalStorage
} from 'utils/fuctions/localStorage';
import { FlexTableV2ColumnVisibilityToggle } from './ColumnVisibilityToggle';
import { FlexTableV2SettingsDropdown } from './SettingsDropdown';
import { getFlexSelectCustomStyle } from '../../assets/styles/multiple_select';
import Select from 'react-select';
import { Filter } from './Filter';
import {
  FlexTableV2ClearSettings,
  FlexTableV2MainContainer,
  FlexTableV2PaginationContainer,
  FlexTableV2PaginationSkipToPage,
  FlexTableV2Resizer,
  FlexTableV2ScrollContainer,
  FlexTableV2SettingsContainer,
  FlexTableV2Table,
  FlexTableV2TBody,
  FlexTableV2TD,
  FlexTableV2THead,
  FlexTableV2TH,
  FlexTableV2TR
} from './styles';

/**
 * Helper para o checkbox com estado "indeterminado", usado no header da tabela para "selecionar todos".
 * @param {{ indeterminate?: boolean } & React.HTMLProps<HTMLInputElement>} props
 */
const IndeterminateCheckbox = ({ indeterminate, ...rest }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof indeterminate === 'boolean' && ref.current) {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);

  return <input type="checkbox" ref={ref} {...rest} />;
};

/**
 * Recebe um objeto de ColumnDef e retorna todos os ids recursivamente (o da coluna pai e o das sub colunas)
 * @param {Object} col
 * @returns {string[]}
 */
function getAllColumnIds(col) {
  const res = [col?.id ?? col?.accessorKey];
  const depth = col?.columns?.flatMap?.((c) => getAllColumnIds(c)) ?? [];

  return res.concat(depth);
}

/**
 * FlexTableV2 - Componente de tabela flexível e rica em funcionalidades
 *
 * @description
 * Renderiza uma tabela interativa utilizando `@tanstack/react-table` como motor principal.
 * É projetada para ser altamente configurável e stateless, delegando o controle do estado ao componente pai.
 * Suporta funcionalidades como:
 * - Paginação (server-side por padrão)
 * - Ordenação
 * - Filtros por coluna
 * - Seleção de linhas
 * - Reordenação de colunas (drag-and-drop)
 * - Redimensionamento de colunas
 * - Visibilidade de colunas
 * - Persistência de estado no localStorage
 *
 * @component
 *
 * @requires @tanstack/react-table
 *
 * @importante
 * Requisitos de uso:
 * 1. O componente é stateless. Todo o estado (paginação, ordenação, etc.) deve ser gerenciado pelo componente pai.
 * 2. As props `table` (definição) e `state` (estado e setters) são obrigatórias. Também são essas as únicas props aceitas para esse componente. Não se deve adicionar mais props.
 * 3. Funcionalidades são habilitadas ao fornecer o estado e seu respectivo setter. Por exemplo, para habilitar a seleção de linhas, é necessário passar `rowSelection` e `setRowSelection` dentro do objeto `state`.
 *
 * @filosofia
 * Este componente deve ser **livre de estado interno**. Toda a gestão de estado (paginação, ordenação, filtros, seleção, etc.) é de **responsabilidade do componente pai**.
 * O `FlexTableV2` recebe o estado e as funções de atualização via props e os conecta à instância da tabela. Isso garante um fluxo de dados unidirecional (top-down), tornando o comportamento da tabela previsível, fácil de depurar e de integrar com outras lógicas de estado da aplicação (como Redux ou Zustand).
 *
 * @exemplo
 * import React, { useState } from 'react';
 *
 * function MinhaPagina() {
 * // 1. Gerenciamento de todo o estado no componente pai
 * const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
 * const [sorting, setSorting] = useState([]);
 * const [columnFilters, setColumnFilters] = useState([]);
 * const [rowSelection, setRowSelection] = useState({});
 * const [columnOrder, setColumnOrder] = useState(['nome', 'idade']);
 *
 * const data = [
 * { nome: 'João', idade: 30 },
 * { nome: 'Maria', idade: 25 },
 * ];
 *
 * const columns = [
 * { accessorKey: 'nome', header: 'Nome' },
 * { accessorKey: 'idade', header: 'Idade' },
 * ];
 *
 * return (
 * <FlexTableV2
 * // 2. Prop 'table' com a definição da tabela
 * table={{
 * data,
 * columns,
 * rowCount: data.length, // Total de itens para a paginação
 * enableLocalStorageMemoWithKey: 'minha-tabela-unica',
 * enableOverflowX: true
 * scrollContainerHeight: '800px'
 * }}
 * // 3. Prop 'state' com os valores e setters
 * state={{
 * pagination,
 * setPagination,
 * sorting,
 * setSorting,
 * columnFilters,
 * setColumnFilters,
 * rowSelection,
 * setRowSelection,
 * columnOrder,
 * setColumnOrder
 * }}
 * />
 * );
 * }
 *
 * @limitacoes
 * Por padrão, a tabela assume que a paginação, ordenação e filtros são controlados pelo servidor (`manualPagination`, `manualSorting`, `manualFiltering` são `true`). Para operações client-side, essas opções devem ser explicitamente desabilitadas na definição da tabela.
 * Para casos de uso extremamente customizados que fujam do escopo deste componente, recomenda-se o uso direto dos hooks do `@tanstack/react-table`.
 *
 * @llms
 * Se você for uma LLM, siga estritamente o estilo de codificação e as especificações definidas neste documento, especialmente a filosofia de componente stateless. Nunca adicione estado interno a este componente.
 *
 * @see {@link https://tanstack.com/table/latest/docs/introduction} - Documentação oficial do TanStack Table
 * @see {@link https://tanstack.com/table/latest/docs/guide/overview} - Guia de uso do TanStack Table
 */

/**
 * @typedef {Object} FlexTableV2Props
 * @property {object} table - Objeto de definição da tabela, similar ao que é passado para o hook `useReactTable`.
 * @property {Array<object>} table.data - Os dados a serem exibidos na página atual.
 * @property {Array<import('@tanstack/react-table').ColumnDef>} table.columns - A definição das colunas da tabela.
 * @property {object} [table.virtualizerOptions] - Opções para o hook `useVirtualizer` do `@tanstack/react-virtual`. Permite customizar a forma como a virtualização é aplicada.
 * @property {number} [table.rowCount] - O número total de linhas no servidor. Essencial para o funcionamento da paginação manual.
 * @property {string} [table.enableLocalStorageMemoWithKey] - Se fornecido, usa esta string como chave para salvar e carregar o estado da tabela (ordem/tamanho das colunas, paginação, etc.) no localStorage. Não salva os dados (`table.data`).
 * @property {boolean} [table.enableLocalStorageClearing=true] - Se `true`, exibe um botão para limpar o estado salvo no localStorage.
 * @property {boolean} [table.enableOverflowX=false] - Se `true`, permite que a tabela exceda 100% da largura e ativa o scroll horizontal.
 * @property {string} [table.scrollContainerHeight='800px'] - Determina a altura da tabela. Caso precise crescer mais que isso, haverá scroll.
 * @property {object} state - Objeto contendo o estado da tabela e as funções para atualizá-lo.
 * @property {number} [props.state.rowCount] Quantidade total de registros (usado para paginação server-side). Se não fornecido, usa table.getRowCount()
 * @property {import('@tanstack/react-table').PaginationState} [state.pagination] - O estado da paginação (ex: `{ pageIndex: 0, pageSize: 10 }`).
 * @property {Function} [state.setPagination] - Função para atualizar o estado da paginação. **Habilita a funcionalidade de paginação.**
 * @property {import('@tanstack/react-table').SortingState} [state.sorting] - O estado da ordenação (ex: `[{ id: 'colunaId', desc: true }]`).
 * @property {Function} [state.setSorting] - Função para atualizar o estado da ordenação. **Habilita a funcionalidade de ordenação.**
 * @property {import('@tanstack/react-table').ColumnFiltersState} [state.columnFilters] - O estado dos filtros (ex: `[{ id: 'colunaId', value: 'valor' }]`).
 * @property {Function} [state.setColumnFilters] - Função para atualizar o estado dos filtros. **Habilita a funcionalidade de filtro.**
 * @property {import('@tanstack/react-table').RowSelectionState} [state.rowSelection] - O estado da seleção de linhas (ex: `{ '0': true, '2': true }`).
 * @property {Function} [state.setRowSelection] - Função para atualizar o estado da seleção. **Habilita a funcionalidade de seleção de linhas.**
 * @property {import('@tanstack/react-table').ColumnOrderState} [state.columnOrder] - O estado da ordem das colunas (ex: `['col2', 'col1', 'col3']`).
 * @property {Function} [state.setColumnOrder] - Função para atualizar a ordem das colunas. **Habilita a funcionalidade de reordenação de colunas.**
 * @property {import('@tanstack/react-table').ColumnSizingState} [state.columnSizing] - O estado dos tamanhos das colunas.
 * @property {Function} [state.setColumnSizing] - Função para atualizar o tamanho das colunas. **Habilita a funcionalidade de redimensionamento de colunas.**
 * @property {import('@tanstack/react-table').VisibilityState} [state.columnVisibility] - O estado de visibilidade das colunas (ex: `{ 'colunaId': false }`).
 * @property {Function} [state.setColumnVisibility] - Função para atualizar a visibilidade das colunas. **Habilita o seletor de visibilidade de colunas.**
 */

/**
 * @function
 * @param {FlexTableV2Props} props
 * @returns {React.FC | null}
 */
export const FlexTableV2 = memo(
  ({ state, table: tableDef }) => {
    const [reorderingMode, setReorderingMode] = useState(() => {
      try {
        const saved = localStorage.getItem('flex-table-v2-reordering-mode');
        return saved === 'shift' || saved === 'swap' ? saved : 'shift';
      } catch (e) {
        return 'shift';
      }
    });

    const [isConfigDropdownOpen, setIsConfigDropdownOpen] = useState(false);

    const handleSetReorderingMode = useCallback((mode) => {
      setReorderingMode(mode);
      localStorage.setItem('flex-table-v2-reordering-mode', mode);
      window.dispatchEvent(
        new CustomEvent('flex-table-reordering-mode-changed', {
          detail: mode
        })
      );
    }, []);

    const toggleConfigDropdown = useCallback(() => {
      setIsConfigDropdownOpen((prev) => !prev);
    }, []);

    useEffect(() => {
      const handleCustomChange = (e) => {
        if (e.detail === 'shift' || e.detail === 'swap') {
          setReorderingMode(e.detail);
        }
      };
      const handleStorageChange = (e) => {
        if (e.key === 'flex-table-v2-reordering-mode') {
          const newVal = e.newValue;
          if (newVal === 'shift' || newVal === 'swap') {
            setReorderingMode(newVal);
          }
        }
      };

      window.addEventListener(
        'flex-table-reordering-mode-changed',
        handleCustomChange
      );
      window.addEventListener('storage', handleStorageChange);

      return () => {
        window.removeEventListener(
          'flex-table-reordering-mode-changed',
          handleCustomChange
        );
        window.removeEventListener('storage', handleStorageChange);
      };
    }, []);

    /** Indica se o estado da tabela deve ser buscado do localStorage no primeiro render */
    const shouldLoadFromLocalStorageMemo = useRef(
      !!tableDef?.enableLocalStorageMemoWithKey
    );

    const tableContainerRef = useRef(null);

    const {
      columnOrder,
      setColumnOrder,
      pagination,
      setPagination,
      rowCount,
      setColumnSizing,
      setSorting,
      sorting,
      columnVisibility,
      setColumnVisibility,
      columnFilters,
      setColumnFilters,
      rowSelection,
      setRowSelection
    } = (() => {
      if (shouldLoadFromLocalStorageMemo.current) {
        // Calcula os valores padrões do estado para o caso de o state fornecido não possuir referencia a eles
        const defaultColumnOrder = tableDef?.columns?.map?.(
          (c) => c.id ?? c.accessorKey
        );
        const defaultPagination = { pageIndex: 0, pageSize: 10 };
        const defaultColumnVisibility =
          tableDef?.columns?.reduce?.((acc, col) => {
            const colIds = getAllColumnIds(col);

            colIds.map((id) => (acc[id] = true));

            return acc;
          }, {}) ?? {};

        // Carrega o localStorage para buscar estado que possa estar salvo
        // Se não encontrar, dá prioridade ao estado passado via props
        // Caso não haja estado salvo nem estado passado, utiliza os valores padrão calculados anteriormente
        let {
          columnOrder = state?.columnOrder ?? defaultColumnOrder,
          pagination = state?.pagination ?? defaultPagination ?? {},
          columnVisibility = state?.columnVisibility ?? defaultColumnVisibility,
          columnFilters = state?.columnFilters ?? [],
          sorting = state?.sorting ?? []
        } = loadObjectFromLocalStorage(
          tableDef.enableLocalStorageMemoWithKey
        ) ?? {};

        // Garante que a coluna 'select' esteja sempre em primeiro
        // lugar quando a seleção de linhas e a reordenação estão ativas.
        if (
          state.setRowSelection &&
          columnOrder &&
          !columnOrder.includes('select')
        ) {
          columnOrder = ['select', ...columnOrder];
        }

        // Caso encontre estado salvo ou passado por props, seta o estado atual para eles
        if (columnOrder?.length && state?.setColumnOrder)
          state.setColumnOrder(columnOrder);
        if (Object.keys(pagination)?.length && state?.setPagination)
          state.setPagination(pagination);
        if (Object.keys(columnVisibility)?.length && state?.setColumnVisibility)
          state.setColumnVisibility(columnVisibility);
        if (sorting?.length && state?.setSorting) state.setSorting(sorting);

        // Desativa a ref que instrui o componente a ler o localStorage, garantindo que todo o processo acima só ocorra no primeiro render
        shouldLoadFromLocalStorageMemo.current = false;
      }

      // Caso o dev opte por salvar o estado no localStorage, faz isso
      if (tableDef?.enableLocalStorageMemoWithKey) {
        const {
          rowSelection: _rs,
          setRowSelection: _srs,
          ...stateToSave
        } = state;
        saveObjectToLocalStorage(
          tableDef.enableLocalStorageMemoWithKey,
          stateToSave
        );
      }
      // Retorna o estado, seja o atual ou o carregado do localStorage (caso seja o primeiro render e o enableLocalStorageMemo seja verdadeiro)
      return state;
    })();

    const handleResetLocalStorage = useCallback(() => {
      localStorage.removeItem(tableDef.enableLocalStorageMemoWithKey);

      toast.success('Memória limpa com sucesso! Recarregando...', {
        autoClose: 3000,
        closeOnClick: true
      });

      window.location.reload();
    }, [tableDef.enableLocalStorageMemoWithKey]);

    const columns = useMemo(() => {
      if (setRowSelection) {
        const selectionColumn = {
          id: 'select',
          size: 40,
          minSize: 40,
          maxSize: 40,
          enableResizing: false,
          enableSorting: false,
          header: (props) => {
            const { table } = props ?? {};
            if (!table) return null;

            return (
              <IndeterminateCheckbox
                {...{
                  checked: table.getIsAllPageRowsSelected(),
                  indeterminate: table.getIsSomePageRowsSelected(),
                  onChange: table.getToggleAllPageRowsSelectedHandler()
                }}
              />
            );
          },
          cell: (props) => {
            const { row } = props ?? {};
            if (!row) return null;

            return (
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler()
                }}
              />
            );
          }
        };
        return [selectionColumn, ...(tableDef.columns ?? [])];
      }
      return tableDef.columns;
    }, [tableDef.columns, setRowSelection]);

    const table = useReactTable({
      ...tableDef,
      columns,
      state: {
        columnOrder,
        pagination,
        sorting,
        columnVisibility,
        columnFilters,
        rowSelection: rowSelection ?? {}
      },
      enableColumnResizing: !!setColumnSizing,
      enableRowSelection: !!setRowSelection,
      onRowSelectionChange: setRowSelection,
      onColumnFiltersChange: setColumnFilters,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
      getFacetedMinMaxValues: getFacetedMinMaxValues(),
      rowCount: rowCount ?? tableDef?.rowCount,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      manualPagination: true, // por padrão, vou implementar apenas paginação server-side, por enquanto; caso haja necessidade, pode ser implementada client-side
      manualSorting: tableDef?.manualSorting ?? true,
      manualFiltering: tableDef?.manualFiltering ?? true
    });

    const columnSizeVars = useMemo(() => {
      const headers = table.getFlatHeaders();

      const { cssVars, sizes } = headers.reduce(
        (acc, header) => {
          const size = header.getSize();
          const colSize = header.column.getSize();

          const safeHeaderId = (header.id || '').replace(
            /[^a-zA-Z0-9_-]/g,
            '_'
          );
          const safeColId = (header.column.id || '').replace(
            /[^a-zA-Z0-9_-]/g,
            '_'
          );

          acc.sizes[header.id] = size;
          acc.cssVars[`--header-${safeHeaderId}-size`] = size;
          acc.cssVars[`--col-${safeColId}-size`] = colSize;

          return acc;
        },
        { cssVars: {}, sizes: {} }
      );

      if (setColumnSizing) {
        setColumnSizing(sizes);
      }
      return cssVars;
    }, [
      table.getState().columnSizingInfo,
      table.getState().columnSizing,
      setColumnSizing
    ]);

    return (
      <>
        <FlexTableV2SettingsContainer>
          <FlexTableV2SettingsDropdown
            isConfigDropdownOpen={isConfigDropdownOpen}
            toggleConfigDropdown={toggleConfigDropdown}
            reorderingMode={reorderingMode}
            setReorderingMode={handleSetReorderingMode}
          />

          {/** Renderiza um botão que permite limpar o estado salvo em localStorage. Controlado pelo `enableLocalStorageClearing` */}
          {tableDef?.enableLocalStorageClearing ?? true ? (
            <FlexTableV2ClearSettings
              onClick={handleResetLocalStorage}
              title="Limpa as preferências salvas da tabela. Pode ser util em caso de erros na tabela ou para retorná-la para o estado inicial. Recarregará a página para aplicar os efeitos."
            >
              <AiOutlineClear />
              <span>Limpar memória</span>
            </FlexTableV2ClearSettings>
          ) : null}
          {/* Permite controlar a exibição do seletor de visibilidade condicionalmente, levando em conta a presença de `setColumnVisibility` */}
          {setColumnVisibility ? (
            <FlexTableV2ColumnVisibilityToggle
              columns={table.getAllLeafColumns()}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
            />
          ) : null}
        </FlexTableV2SettingsContainer>
        <FlexTableV2MainContainer>
          <FlexTableV2ScrollContainer
            ref={tableContainerRef}
            $overflowX={tableDef?.enableOverflowX}
            $scrollContainerHeight={tableDef?.scrollContainerHeight}
          >
            <FlexTableV2Table
              style={columnSizeVars}
              $overflowX={tableDef?.enableOverflowX}
            >
              <FlexTableV2Content
                table={table}
                setColumnOrder={setColumnOrder}
                setSorting={setSorting}
                columnOrder={columnOrder}
                enableOverflowX={tableDef?.enableOverflowX}
                dataDependency={tableDef}
                tableContainerRef={tableContainerRef}
                virtualizerOptions={tableDef.virtualizerOptions}
                reorderingMode={reorderingMode}
              />
            </FlexTableV2Table>
          </FlexTableV2ScrollContainer>
          {pagination &&
          'pageIndex' in pagination &&
          pagination?.pageSize &&
          setPagination ? (
            <FlexTableV2PaginationContainer>
              <Pagination
                activePage={pagination.pageIndex + 1}
                itemsCountPerPage={pagination.pageSize}
                totalItemsCount={rowCount ?? table.getRowCount()}
                onChange={(pageNumber) =>
                  setPagination({ ...pagination, pageIndex: pageNumber - 1 })
                }
              />

              <FlexTableV2PaginationSkipToPage>
                {!tableDef.hidePaginationPageSelect && (
                  <span>
                    Ir para pág.:
                    <input
                      type="number"
                      min="1"
                      max={table.getPageCount() || 1}
                      value={table.getState().pagination.pageIndex + 1}
                      onChange={(e) => {
                        let val = e.target.value ? Number(e.target.value) : '';
                        if (val !== '' && val > table.getPageCount()) {
                          val = table.getPageCount();
                        }
                        if (val !== '' && val < 1) {
                          val = 1;
                        }
                        const page = val ? Number(val) - 1 : 0;
                        setPagination({ ...pagination, pageIndex: page });
                      }}
                      style={{
                        marginInlineStart: '.5rem',
                        width: '3rem',
                        textAlign: 'center',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        outline: 'none',
                        fontSize: '0.875rem',
                        fontFamily: 'Open Sans, sans-serif',
                        height: '100%'
                      }}
                    />
                  </span>
                )}
                {!tableDef.hidePaginationSizeSelect && (
                  <Select
                    styles={{
                      ...getFlexSelectCustomStyle({
                        width: 'max-content',
                        minWidth: 'max-content',
                        minHeight: '34px',
                        maxHeight: 'none'
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        minWidth: '12ch'
                      })
                    }}
                    value={{
                      label: `Mostrar ${table.getState().pagination.pageSize}`,
                      value: Number(table.getState().pagination.pageSize)
                    }}
                    onChange={(selected) => {
                      setPagination({
                        pageIndex: 0,
                        pageSize: selected.value
                      });
                    }}
                    isSearchable={false}
                    menuPlacement="auto"
                    options={[10, 20, 30, 40, 50].map((pageSize) => ({
                      label: `Mostrar ${pageSize}`,
                      value: pageSize
                    }))}
                  />
                )}
              </FlexTableV2PaginationSkipToPage>
            </FlexTableV2PaginationContainer>
          ) : null}
        </FlexTableV2MainContainer>
      </>
    );
  },
  (prev, next) => {
    return JSON.stringify(prev) === JSON.stringify(next);
  }
);
FlexTableV2.displayName = 'FlexTableV2';

const FlexTableV2Content = memo(
  ({
    table,
    columnOrder,
    setColumnOrder,
    setSorting,
    tableContainerRef,
    virtualizerOptions,
    enableOverflowX,
    reorderingMode
  }) => {
    const rowRefsMap = useRef(new Map());

    const { rows } = table.getRowModel();

    const rowVirtualizer = useVirtualizer({
      count: rows.length,
      estimateSize: () => 44,
      getScrollElement: () => tableContainerRef.current,
      measureElement:
        typeof window !== 'undefined' &&
        navigator.userAgent.indexOf('Firefox') === -1
          ? (element) => element?.getBoundingClientRect().height
          : undefined,
      overscan: 5,
      ...virtualizerOptions,
      onChange: (instance) => {
        instance.getVirtualItems().forEach((virtualRow) => {
          const rowRef = rowRefsMap.current.get(virtualRow.index);
          if (!rowRef) return;
          rowRef.style.transform = `translateY(${virtualRow.start}px)`;
        });
      }
    });

    useLayoutEffect(() => {
      rowVirtualizer.measure();
    }, [table.getState()]);

    const virtualRows = rowVirtualizer.getVirtualItems();

    const visibleColumnOrder = useMemo(() => {
      const visibleColumns = new Set(
        table.getVisibleLeafColumns().map((c) => c.id)
      );
      return (columnOrder ?? []).filter((id) => visibleColumns.has(id));
    }, [columnOrder, table.getState().columnVisibility]);

    const handleVisibleColumnReorder = useCallback(
      (newVisibleOrder) => {
        const hiddenColumns = (columnOrder ?? []).filter(
          (id) => !visibleColumnOrder.includes(id)
        );
        setColumnOrder([...newVisibleOrder, ...hiddenColumns]);
      },
      [columnOrder, visibleColumnOrder, setColumnOrder]
    );

    const {
      handleDragStart,
      handleDragEnd,
      handleDragEnter,
      handleDragLeave,
      handleDragOver
    } = useDragAndReorder(
      visibleColumnOrder,
      handleVisibleColumnReorder,
      undefined,
      {
        reorderingMode
      }
    );

    const ungroupedColumns = (table?.getAllLeafColumns?.() ?? [])
      .filter((col) => !col.parent)
      .map((col) => col.id);

    const leafColumns = table?.getAllLeafColumns?.()?.map?.((c) => c.id) ?? [];

    return (
      <>
        <FlexTableV2THead>
          {table.getHeaderGroups().map((headerGroup, headerGroupIndex) => (
            <FlexTableV2TR key={headerGroup.id} $overflowX={enableOverflowX}>
              {headerGroup.headers.map((header, index) => {
                const dragProps =
                  columnOrder?.length &&
                  ungroupedColumns?.includes?.(header?.column?.id)
                    ? {
                        draggable: true,
                        onDragStart: (e) => handleDragStart(e, index),
                        onDragEnter: (e) => handleDragEnter(e, index),
                        onDragLeave: handleDragLeave,
                        onDragOver: handleDragOver,
                        onDragEnd: handleDragEnd
                      }
                    : {};

                return (
                  <FlexTableV2TH
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: `calc(var(--header-${(header?.id || '').replace(
                        /[^a-zA-Z0-9_-]/g,
                        '_'
                      )}-size) * 1px)`
                    }}
                    $overflowX={enableOverflowX}
                    {...dragProps}
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          style={{
                            inset: 0,
                            cursor: header.column.getCanSort()
                              ? 'pointer'
                              : undefined,
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                          data-has-content={!!header.column.columnDef.header}
                          data-is-display={
                            typeof header.column.accessorFn === 'undefined'
                          }
                          data-is-leaf={leafColumns.includes(
                            header.column.columnDef.accessorKey
                          )}
                          data-row-modulo={headerGroupIndex % 2}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}

                          {header.column.getCanSort() && setSorting && (
                            <div style={{ whiteSpace: 'nowrap' }}>
                              <FaLongArrowAltDown
                                opacity={
                                  header.column.getIsSorted() === 'desc'
                                    ? 1
                                    : 0.5
                                }
                              />

                              <FaLongArrowAltUp
                                opacity={
                                  header.column.getIsSorted() === 'asc'
                                    ? 1
                                    : 0.5
                                }
                                style={{ marginLeft: '-5px' }}
                              />
                            </div>
                          )}
                        </div>

                        {header.column.getCanFilter() &&
                        table.options.onColumnFiltersChange ? (
                          <Filter column={header.column} />
                        ) : null}
                      </>
                    )}

                    {header.column.getCanResize() && (
                      <FlexTableV2Resizer
                        draggable={false}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          header.getResizeHandler()(e);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          header.getResizeHandler()(e);
                        }}
                        onDragStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          return false;
                        }}
                        className={`resizer ${
                          header.column.getIsResizing() ? 'isResizing' : ''
                        }`}
                      />
                    )}
                  </FlexTableV2TH>
                );
              })}
            </FlexTableV2TR>
          ))}
        </FlexTableV2THead>

        <FlexTableV2TBody
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`
          }}
        >
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index];
            const virtualRowIndex = virtualRow.index;
            return (
              <FlexTableV2RowMemo
                key={row.id}
                row={row}
                rowRefsMap={rowRefsMap}
                rowVirtualizer={rowVirtualizer}
                virtualRowIndex={virtualRowIndex}
                virtualRow={virtualRow}
                $overflowX={enableOverflowX}
              />
            );
          })}
        </FlexTableV2TBody>
      </>
    );
  },
  (prev, next) => {
    return (
      prev.dataDependency === next.dataDependency &&
      prev.reorderingMode === next.reorderingMode
    );
  }
);
FlexTableV2Content.displayName = 'FlexTableV2Content';

const FlexTableV2Row = ({
  row,
  rowRefsMap,
  rowVirtualizer,
  virtualRowIndex,
  virtualRow,
  $overflowX
}) => {
  return (
    <FlexTableV2TR
      data-index={virtualRowIndex}
      ref={(node) => {
        if (node && typeof virtualRowIndex !== 'undefined') {
          rowVirtualizer.measureElement(node);
          rowRefsMap.current.set(virtualRowIndex, node);
        }
      }}
      key={row.id}
      style={{
        transform: `translateY(${virtualRow.start}px)`
      }}
      $overflowX={$overflowX}
    >
      {row.getVisibleCells().map((cell) => {
        return (
          <FlexTableV2TD
            key={cell.id}
            style={{
              width: `calc(var(--col-${(cell.column.id || '').replace(
                /[^a-zA-Z0-9_-]/g,
                '_'
              )}-size) * 1px)`
            }}
            $overflowX={$overflowX}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </FlexTableV2TD>
        );
      })}
    </FlexTableV2TR>
  );
};

const FlexTableV2RowMemo = memo(
  FlexTableV2Row,
  (prev, next) =>
    next.rowVirtualizer.isScrolling && prev.$overflowX === next.$overflowX
);
